import { MotiView, useDynamicAnimation } from "moti";
import React from "react";
import { LayoutChangeEvent, Platform } from "react-native";
import { useSharedValue } from "react-native-reanimated";

import { Theme } from "@whoosh/design-system/src/theme/restyle/theme";

import { Box } from "../../primitive";

type AnimateHeightProps = {
  children?: React.JSX.Element;
  hide?: boolean;
  onHeightDidAnimate?: (height: number) => void;
  enterFrom?: "bottom" | "top";
  initialHeight?: number;
  pt?: keyof Theme["spacing"];
} & React.ComponentProps<typeof MotiView>;

export const AnimateHeight = ({
  children,
  hide = false,
  style,
  transition = { height: { type: "timing", delay: 0 } },
  enterFrom = "top",
  onHeightDidAnimate,
  initialHeight = 0,
  pt = 6,
  ...motiViewProps
}: AnimateHeightProps) => {
  const measuredHeight = useSharedValue(initialHeight);

  const state = useDynamicAnimation(() => {
    if (!hide) {
      return {
        height: 0,
        opacity: 1,
      };
    }
    return {
      height: initialHeight,
      opacity: initialHeight ? 1 : 0,
    };
  });

  const onLayoutHandler = (event: LayoutChangeEvent) => {
    const { nativeEvent } = event;
    measuredHeight.value = nativeEvent.layout.height;

    if (!hide) {
      state.animateTo({
        height: nativeEvent.layout.height,
        opacity: 1,
      });
    }
  };

  const height = hide ? 0 : Math.ceil(measuredHeight.value);

  state.animateTo({
    height,
    opacity: height ? 1 : 0,
  });

  return (
    <MotiView
      {...motiViewProps}
      state={state}
      transition={transition}
      onDidAnimate={
        onHeightDidAnimate &&
        ((key, finished, _, { attemptedValue }) =>
          key === "height" && onHeightDidAnimate(attemptedValue as number))
      }
      style={{
        overflow:
          Platform.OS === "web" ? (hide ? "hidden" : "visible") : "scroll",
      }}
    >
      <Box onLayout={onLayoutHandler} pt={pt}>
        {children}
      </Box>
    </MotiView>
  );
};
